import { useState, useEffect } from "react";
import { App } from "antd";

import { getItem } from "@/api/reports";
import { getStatusOptions } from "@/api/statuses";
import { getUnitEmployees } from "@/api/employees";
import { getEmployeeUnitsOptions } from "@/api/units";
import { getObjectsOptions } from "@/api/objects";

// Нормализация списка сотрудников в формат { key, value, label }
const formatEmployeeOptions = (employees) => {
  return employees.map((employee) => ({
    key: employee.uuid,
    value: employee.uuid,
    label: [employee.lastname, employee.firstname, employee.patronymic]
      .filter(Boolean)
      .join(" "),
  }));
};

/**
 * useReportData — загружает конфигурацию отчёта и справочные данные.
 *
 * Параметры:
 *  - reportId: uuid отчёта
 *
 * Возвращает:
 *  - data: конфигурация отчёта (имя, фильтры, и т.п.)
 *  - pending: флаг загрузки
 *  - statusOptions, employeesOptions, unitsOptions: справочники
 *  - objectsOptions: объекты по definition_uuid, например { [uuid]: options[] }
 */
export const useReportData = (reportId) => {
  const { message } = App.useApp();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(true);
  const [statusOptions, setStatusOptions] = useState([]);
  const [employeesOptions, setEmployeesOptions] = useState([]);
  const [unitsOptions, setUnitsOptions] = useState([]);
  const [objectsOptions, setObjectsOptions] = useState({});

  useEffect(() => {
    // Загружает список объектов для каждого фильтра типа "object"
    const fetchObjectsOptions = async (filters) => {
      const objectFilters = filters.filter(({ type }) => type === "object");
      const results = await Promise.all(
        objectFilters.map(({ object_uuid }) =>
          getObjectsOptions({ definition_uuid: object_uuid })
        )
      );

      return results.reduce((acc, result) => {
        acc[result.definition.uuid] = result.objects;
        return acc;
      }, {});
    };

    const fetchData = async () => {
      setPending(true);

      try {
        // Загружаем данные параллельно: конфигурация отчёта и справочники
        const [reportData, statuses, { members }, { units }] =
          await Promise.all([
            getItem(reportId),
            getStatusOptions(),
            getUnitEmployees(),
            getEmployeeUnitsOptions(),
          ]);

        setData(reportData);
        setStatusOptions(statuses);
        setEmployeesOptions(formatEmployeeOptions(members));
        setUnitsOptions(units);

        // Отдельно получаем словари объектов для нужных фильтров
        const objects = await fetchObjectsOptions(reportData?.filters || []);
        setObjectsOptions(objects);
      } catch (error) {
        message.error(error?.message || "Ошибка загрузки данных");
      } finally {
        setPending(false);
      }
    };

    fetchData();
  }, [reportId, message]);

  return {
    data,
    pending,
    statusOptions,
    employeesOptions,
    unitsOptions,
    objectsOptions,
  };
};
